import { message } from 'antd';
import {
  CasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1CaseDetailsApplicationContainerEntitiesDocument,
  CasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1UpdateDocument,
  CredentialVerificationCaseV1,
  CredentialVerificationCaseV1Application,
  CredentialVerificationCaseV1RevisionRequest,
  Entity
} from '../../../../../../../../generated';
import { CaseDetailsApplication } from './case-details-application';
import { useMutation, useQuery } from '@apollo/client';
import { lookupCountryQuery } from './case-details-country-query';
import { Country } from '../../../../../../../../types/country';
import { ComponentQueryLoader } from '../../../../../../../shared/component-query-loader';
import { useEffect } from 'react';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';

interface CaseDetailsApplicationContainerProps {
  caseId: string;
  applicantId: string;
}
export const CaseDetailsApplicationContainer: React.FC<CaseDetailsApplicationContainerProps> = (props) => {
  useEffect(() => {
    console.log('Application Container Loaded');
  }, []);

  const { data, loading, error, refetch } = useQuery(CasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1Document, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    }
  });

  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  const {
    data: entities,
    loading: entitiesLoading,
    error: entitiesError
  } = useQuery(CasesCredentialVerificationV1CaseDetailsApplicationContainerEntitiesDocument, {
    context: {
      headers: {
        'cache-enabled': 'true'
      }
    }
  });

  const [updateCredentialVerificationCase] = useMutation(CasesCredentialVerificationV1CaseDetailsApplicationContainerCredentialVerificationCaseV1UpdateDocument);

  const handleOnUploadSuccess = async (response: AzureBlobResponse, fieldName: string) => {
    const input = {
      id: props.caseId,
      caseDetails: {
        application: {
          credentialAssets: {
            [fieldName]: response.blobUrl,
            [`${fieldName}Version`]: response.versionId
          }
        }
      }
    };
    try {
      await updateCredentialVerificationCase({ variables: { input } }).then((result) => {
        if (result.data?.credentialVerificationCaseV1Update?.status.success) {
          message.success('File uploaded successfully');
        } else {
          message.error('Error uploading file');
        }
      })
    } catch (error) {
      message.error('File upload failed. Please try again.');
    }
  };

  const updateApplicationInput = (input: CredentialVerificationCaseV1Application) => ({
    isCredentialInEnglish: input.isCredentialInEnglish,
    credentialType: input.credentialType,
    credentialTitle: input.credentialTitle,
    program: input.program,
    nameOnCredential: input.nameOnCredential,
    dateCredentialIssuedAt: input.dateCredentialIssuedAt,
    issuingInstitution: input.otherIssuingInstitution ? null : input.issuingInstitution,
    otherIssuingInstitution: input.otherIssuingInstitution
      ? {
          addressLine1: input.otherIssuingInstitution?.addressLine1,
          addressLine2: input.otherIssuingInstitution?.addressLine2,
          city: input.otherIssuingInstitution?.city,
          country: input.otherIssuingInstitution?.country,
          institutionName: input.otherIssuingInstitution?.institutionName,
          stateOrProvince: input.otherIssuingInstitution?.stateOrProvince,
          zipOrPostalCode: input.otherIssuingInstitution?.zipOrPostalCode
        }
      : null,
    sendDestination: input.sendDestination
  });

  const onSave = async (input: CredentialVerificationCaseV1Application) => {
    try {
      const result = await updateCredentialVerificationCase({
        variables: {
          input: {
            id: props.caseId,
            caseDetails: {
              application: updateApplicationInput(input)
            }
          }
        }
      });
      await refetch();
      if (result.data?.credentialVerificationCaseV1Update?.status.success) {
        message.success('Application data saved successfully');
      } else {
        message.error('Error saving Application data');
      }
    } catch (error) {
      message.error('Error saving Application data');
    }
  };

  const onSaveAndComplete = async (input: CredentialVerificationCaseV1Application, revisionRequest: CredentialVerificationCaseV1RevisionRequest) => {
    try {
      const result = await updateCredentialVerificationCase({
        variables: {
          input: {
            id: props.caseId,
            caseDetails: {
              application: updateApplicationInput(input)
            },
            revisionRequest: {
              requestedChanges: {
                requestUpdatedCredentialDetails: false,
                requestUpdatedIssuingInstitution: false,
                requestUploadCredential: false,
                requestUploadTranslation: false
              },
              revisionSubmittedAt: revisionRequest.revisionSubmittedAt
            }
          }
        }
      });
      await refetch();
      if (result.data?.credentialVerificationCaseV1Update?.status.success) {
        message.success('Application data saved successfully');
      } else {
        message.error('Error saving Application data');
      }
    } catch (error) {
      message.error('Error saving Application');
    }
  };

  return (
    <ComponentQueryLoader
      error={error || countryError || entitiesError}
      loading={loading || countryLoading || entitiesLoading}
      hasData={data}
      hasDataComponent={
        <CaseDetailsApplication
          data={data?.credentialVerificationCaseV1 as CredentialVerificationCaseV1}
          applicantId={props.applicantId}
          application={data?.credentialVerificationCaseV1?.caseDetails?.application as CredentialVerificationCaseV1Application}
          revisionRequested={data?.credentialVerificationCaseV1?.revisionRequest as CredentialVerificationCaseV1RevisionRequest}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          entities={entities?.entities as Entity[]}
          onSave={onSave}
          onSaveAndComplete={onSaveAndComplete}
          handleOnUploadSuccess={handleOnUploadSuccess}
        />
      }
    />
  );
};
